import PageHeader from '../../../components/PageHeader'
import MainLayout from '../../../layouts/MainLayout'
import { usePermissions } from '../../../hooks/usePermissions'
import { useNavigate } from 'react-router-dom'
import ListHeader from '../../../components/ListHeader'
import TableWithInactiveFilter from '../../../components/table/TableWithInactiveFilter'
import useBaseService from '../../../hooks/services/useBaseService'
import { entities } from '../../../constants/entities'
import { useEffect, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import useLoader from '../../../hooks/useLoader'
import useNotification from '../../../hooks/useNotification'
import useQueryWithLoader from '../../../hooks/useQueryWithLoader'
import { formatDate } from '../../../utils/formatDate'
import { faEye, faPrint } from '@fortawesome/free-solid-svg-icons'
import { useReport } from '../../../context/ReportContext'

const downloadPdf = async (url) => {
  const fixUrl = (u) => {
    if (window.location.hostname === 'localhost') return u
    try {
      const parsed = new URL(u)
      return `${window.location.origin}${parsed.pathname}`
    } catch {
      return u
    }
  }

  const finalUrl = fixUrl(url)

  try {
    const response = await fetch(finalUrl)
    if (!response.ok) throw new Error(`HTTP ${response.status}`)
    const blob = await response.blob()
    const blobUrl = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = blobUrl
    link.download = finalUrl.split('/').pop() || 'protocolo.pdf'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(blobUrl)
  } catch (e) {
    console.error('Falha no download via blob, abrindo em aba:', e)
    window.open(finalUrl, '_blank')
  }
}

const InternalExitTransactionsPage = () => {
  const { canAccess } = usePermissions()
  const navigate = useNavigate()
  const { get: baseServiceGet } = useBaseService(navigate)
  const { hideLoader, showLoader } = useLoader()
  const { showNotification } = useNotification()
  const { isExporting } = useReport()

  const {
    data: transactionsData,
    isLoading,
    isFetching,
  } = useQueryWithLoader({
    queryKey: ['almox_internal_exits'],
    queryFn: async () => {
      const params = {
        filters: {
          transaction_type_id: { $eq: 3 }, // DEPARTURE
          is_internal_exit: { $eq: 1 },
          deleted_at: { $eq: null },
        },
        page: 1,
        perPage: 50,
      }
      const response = await baseServiceGet(entities.transactions.get, params)
      return response.result
    },
    refetchOnMount: true,
    select: (data) => ({
      transactions: data?.data?.map((t) => ({
        id: t.id,
        department: t.department || '-',
        transaction_date: t.transaction_date
          ? formatDate({ rawDate: t.transaction_date, showTime: false })
          : '-',
        user: t.user?.name || '-',
      })),
      totalPages: data?.last_page,
    }),
  })

  const { transactions = [], totalPages = 1 } = transactionsData || {}

    const exportProtocol = useMutation({
    mutationFn: async ({ transactionId }) => {
      showLoader()
      const response = await baseServiceGet(
        entities.reports.internalExitProtocol(transactionId)
      )
      return response
    },
    onSuccess: async (response) => {
      if (response?.status === 202 || response?.status === 200) {
        const exportId = response.result?.export_id
        if (!exportId) {
          showNotification('error', 'Não foi possível iniciar a geração.')
          return
        }

        showNotification(
          'info',
          'Processamento do protocolo iniciado em segundo plano.'
        )

        // 🆕 Polling: aguarda o job terminar e baixa o PDF
        for (let i = 0; i < 60; i++) {
          await new Promise((r) => setTimeout(r, 2000))

          const check = await baseServiceGet(
            entities.reports.checkStatus(exportId)
          )
          const res = check?.result ?? check?.data?.result

          if (res?.status === 'completed' && res?.file_path) {
            await downloadPdf(res.file_path)
            showNotification('success', 'Protocolo gerado com sucesso!')
            return
          }

          if (res?.status === 'failed' || res?.status === 'cancelled') {
            showNotification(
              'error',
              res?.message || 'Falha ao gerar o protocolo.'
            )
            return
          }
        }

        showNotification('error', 'Tempo esgotado ao gerar o protocolo.')
      }
    },
    onError: (error) => {
      console.error('Erro ao gerar protocolo:', error)
      showNotification(
        'error',
        error.response?.data?.message || 'Erro ao gerar o protocolo.'
      )
    },
    onSettled: () => hideLoader(),
  })

  const columns = [
    { key: 'id', label: 'Número' },
    { key: 'department', label: 'Departamento' },
    { key: 'transaction_date', label: 'Data da Saída' },
    { key: 'user', label: 'Responsável' },
  ]

  const actions = [
    {
      id: 'exportar_protocolo',
      icon: faPrint,
      title: 'Imprimir Protocolo',
      buttonClass: 'btn-success',
      permission: 'Ver transações',
      onClick: async (t) => {
        if (isExporting) {  // 🆕 bloqueia se já estiver exportando
          showNotification('warning', 'Já existe um relatório sendo gerado.')
          return
        }
        try {
          await exportProtocol.mutateAsync({ transactionId: t.id })
        } catch (error) {
          console.error('Erro ao exportar protocolo:', error)
        }
      },
      show: (t) => canAccess('Ver transações'),
    },
    {
      id: 'details',
      icon: faEye,
      title: 'Ver Detalhes',
      buttonClass: 'btn-info',
      permission: 'Ver transações',
      onClick: (t) => navigate(`/almoxarifado/transacoes/detalhes/${t.id}`),
      show: (t) => canAccess('Ver transações'),
    },
  ]

  useEffect(() => {
    isLoading ? showLoader() : hideLoader()
  }, [isLoading, showLoader, hideLoader])

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader title="Saídas Internas" showBackButton backUrl="/dashboard" />
      <div className="container-fluid p-1">
        <ListHeader
          title="Lista de Saídas Internas"
          buttonText="Cadastrar Saída Interna"
          buttonLink="/almoxarifado/transacoes/saida-interna/criar"
          permission="Criar transações"
          canAccess={canAccess}
        />

        <TableWithInactiveFilter
          data={transactions}
          columns={columns}
          actions={actions}
          totalPages={totalPages}
          currentPage={0}
          onPageChange={() => {}}
          rowsPerPage={50}
          onRowsPerPageChange={() => {}}
          type="default"
          isLoading={isLoading}
          isFetching={isFetching}
        />
      </div>
    </MainLayout>
  )
}

export default InternalExitTransactionsPage